import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';

import { catchError, combineLatest, distinctUntilChanged, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';

import { MenuItem, MessageService } from 'primeng/api';
import { drawerHeaderDesignToken, menubarHeaderDesignToken, menuHeaderDesignToken } from '@tmt/traton-theme';

import { environment } from 'src/environments/environment';
import { FunctionalGroup } from 'src/models/functional-group';
import { AuthenticationMutations } from 'src/graphql/mutations/authentication.mutations';
import { AuthService } from 'src/services/auth.service';
import { LaunchDarklyService } from 'src/services/launchdarkly.service';
import { ApolloService } from 'src/services/apollo.service';
import { NexusService } from 'src/services/nexus.service';
import { ExternalSessionService } from 'src/services/external-session.service';

import { ExternalModeService } from './../../services/external-mode.service';

/**
 * Main header of the application.
 */
@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrl: './header.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
	public title = 'Fenix Test Results';

	public homeUrl = environment.homeUrl;

	public menubarHeaderDesignToken = menubarHeaderDesignToken;

	public menuDesignToken = menuHeaderDesignToken;

	public drawerDesignToken = drawerHeaderDesignToken;

	public isSigningOut: boolean = false;

	public showSettings: boolean = false;

	public showUserDetails: boolean = false;

	public isStaging: boolean = false;

	public breakpoint = '960px';

	public isMobile$ = this.breakpointObserver.observe([`(max-width: ${this.breakpoint})`]).pipe(
		map(result => result.matches),
		distinctUntilChanged(),
	);

	public functionalGroups$: Observable<FunctionalGroup[]> = this.authService.userData$.pipe(
		switchMap(userData => {
			if (!userData.sessionID) return of();
			return this.nexusService.allFunctionalGroups$.pipe(
				map(functionalGroups => {
					return functionalGroups.filter(functionalGroup => userData.functionalGroups.find(userDataFunctionalGroup => userDataFunctionalGroup === functionalGroup.uid));
				}),
			);
		}),
	);

	/**
	 * Staging state.
	 */
	public isStaging$ = this.launchDarklyService.launchDarklyFlags$.pipe(
		map(flags => flags['stagings']),
		shareReplay(1),
	);

	/**
	 * Sign out user
	 */
	public signOut() {
		this.isSigningOut = true;

		this.apolloClientSerivce.authLayerClient
			.pipe(
				take(1),
				switchMap(client =>
					client.mutate<{ signOut: boolean | null }>({
						mutation: AuthenticationMutations.signOut,
					}),
				),
				map(response => response ?? false),
				tap(response => {
					if (response) {
						this.messageService.add({ severity: 'success', life: 2000, summary: 'Successfully signed out' });
						this.authService.clearSession();
						this.externalSessionService.clearSession();

						this.isSigningOut = false;
					} else {
						this.isSigningOut = false;
						throw new Error('Failed when signing out');
					}
				}),
				catchError(error => {
					this.isSigningOut = false;
					this.messageService.add({ severity: 'error', life: 20000, summary: 'Error when signing out', detail: error });
					return of(undefined);
				}),
			)
			.subscribe();
	}

	/**
	 * Menu items in desktop view
	 * No internal navigation
	 */
	public menuItems$: Observable<MenuItem[]> = combineLatest({
		userData: this.authService.userData$,
		canStartExternalSession: this.externalModeService.canStartExternalSession$,
	}).pipe(
		map(source =>
			[
				{
					label: source.userData.firstName + ' ' + source.userData.lastName,
					icon: 'pi pi-user',
					visible: source.userData.sessionID ? true : false,
					command: () => {
						this.showUserDetails = true;
					},
				},
				{
					icon: 'pi pi-question-circle',
					label: 'Support',
					command: () => {
						window.open(environment.supportUrl, '_blank');
					},
				},
				{
					icon: 'pi pi-book',
					label: 'Wiki',
					command: () => {
						window.open(environment.wikiUrl, '_blank');
					},
				},
				{
					icon: 'pi pi-home',
					label: 'Test Tools',
					command: () => {
						window.open(environment.homeUrl, '_self');
					},
				},
				{
					label: source.userData.sessionID ? 'Sign out' : 'Sign in',
					icon: source.userData.sessionID ? 'pi pi-sign-out' : 'pi pi-sign-in',
					command: () => {
						source.userData.sessionID ? this.signOut() : this.authService.login();
					},
				},
			].concat(
				source.canStartExternalSession
					? [
							{
								label: 'Start external session',
								icon: 'pi pi-sign-in',
								command: () => {
									this.externalModeService.toggleExternalMode();
								},
							},
						]
					: [],
			),
		),
	);

	/**
	 * Constructor
	 * @param authService
	 * @param launchDarklyService
	 * @param themeService
	 * @param preferencesService
	 * @param groupAccessService
	 * @param apolloClientSerivce
	 * @param messageService
	 * @param nexusService
	 * @param breakpointObserver
	 * @param xternalModeService
	 * @param externalModeService
	 * @param externalSessionService
	 * @param router
	 */
	constructor(
		public authService: AuthService,
		private launchDarklyService: LaunchDarklyService,
		private apolloClientSerivce: ApolloService,
		private messageService: MessageService,
		private nexusService: NexusService,
		private breakpointObserver: BreakpointObserver,
		public externalModeService: ExternalModeService,
		private externalSessionService: ExternalSessionService,
		private router: Router,
	) {}

	public toggleExternalMode() {
		this.externalModeService.toggleExternalMode();
		this.router.navigate(['/']);
	}
}
